import React, { useState, useRef, useCallback, useEffect } from "react";
import "./RightSidebar.css";

interface RightSidebarProps {
  onWidthChange?: (width: number) => void;
}

const RightSidebar: React.FC<RightSidebarProps> = ({ onWidthChange }) => {
  const [width, setWidth] = useState(500);
  const [height, setHeight] = useState(window.innerHeight - 56);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, width: 0 });
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  const minWidth = 300;
  const maxWidth = Math.min(window.innerWidth * 0.95, 1600);

  // Height adjustment to let chatbot UI fill the window
  const updateHeight = () => {
    const scrollY = window.scrollY;
    const baseHeight = window.innerHeight - 56;
    const maxHeight = window.innerHeight;
    const newHeight = Math.min(baseHeight + scrollY, maxHeight);
    setHeight(newHeight);
  };

  useEffect(() => {
    window.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);
    updateHeight();
    
    return () => {
      window.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    if (onWidthChange) {
      onWidthChange(width);
    }
  }, [width, onWidthChange]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      width: width
    });
  }, [width]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = resizeStart.x - e.clientX; // Negative delta means dragging left (increasing width)
    const newWidth = Math.max(
      minWidth,
      Math.min(maxWidth, resizeStart.width + deltaX)
    );
    
    setWidth(newWidth);
  }, [isResizing, resizeStart, minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const containerStyle: React.CSSProperties = {
    height: `${height}px`,
    width: `${width}px`,
  };

  return (
    <>
      {/* Overlay during resize */}
      {isResizing && <div className="chat-resize-overlay" />}
      
      <div 
        ref={chatContainerRef}
        className={`chat-container ${isResizing ? 'resizing' : ''}`} 
        style={containerStyle}
      >
        {/* Resize handle */}
        <div
          className="chat-resize-handle"
          onMouseDown={handleMouseDown}
        />
        
        <div className="chatbot-container">
          <iframe
            src="https://autoreport.lab126.amazon.dev/"
            title="AI Assistant"
            className="chatbot-iframe"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            loading="lazy"
          />
        </div>
      </div>
    </>
  );
};

export default RightSidebar;
